import { useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

/** Render outside the scrolling table so last-row actions remain reachable. */
export function InstitutionMoreMenu({ children }: { children: ReactNode }) {
  const trigger = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => {
    if (!position) return;
    function dismiss(event: PointerEvent) {
      if (!panel.current?.contains(event.target as Node) && !trigger.current?.contains(event.target as Node)) setPosition(null);
    }
    function keydown(event: KeyboardEvent) {
      if (event.key === "Escape") { setPosition(null); trigger.current?.focus(); }
    }
    function close() { setPosition(null); }
    document.addEventListener("pointerdown", dismiss);
    document.addEventListener("keydown", keydown);
    window.addEventListener("resize", close);
    window.addEventListener("scroll", close, true);
    return () => {
      document.removeEventListener("pointerdown", dismiss);
      document.removeEventListener("keydown", keydown);
      window.removeEventListener("resize", close);
      window.removeEventListener("scroll", close, true);
    };
  }, [position]);

  useEffect(() => {
    if (position) panel.current?.querySelector<HTMLButtonElement>("button")?.focus({ preventScroll: true });
  }, [position]);

  return <>
    <button ref={trigger} type="button" aria-label="更多" aria-expanded={!!position} onClick={() => {
      if (position) { setPosition(null); return; }
      const rect = trigger.current!.getBoundingClientRect();
      setPosition({ left: Math.max(8, Math.min(rect.right - 140, window.innerWidth - 148)),
        top: rect.bottom + 148 < window.innerHeight ? rect.bottom + 8 : Math.max(8, rect.top - 148) });
    }}>更多 ▾</button>
    {position && createPortal(<div ref={panel} className="platform-institution-actions-popover" role="group" aria-label="机构更多操作" style={position} onClick={() => setPosition(null)}>{children}</div>, document.body)}
  </>;
}
